/**
 * Acquiring the microphone, and saying so when it cannot be acquired.
 *
 * Every way this fails looks identical to a visitor — they click the mic
 * button and nothing happens — so each one has to name itself. The three
 * outcomes that matter are: the browser has no capture API at all, the
 * visitor (or their IT policy) refused, and the device would not open.
 *
 * Store writes go through `bannerPrecedence.showBanner` rather than
 * `setActiveBanner`, so a spent-credits banner is never replaced by a mic
 * hint that cannot help.
 */

import { detectVoiceCapability } from '@/voice/availability';
import { clearBannerKinds, MIC_OWNED_KINDS, showBanner } from '@/voice/bannerPrecedence';
import { makeMicPermissionDeniedBanner } from '@/voice/fallbacks/micPermissionDenied';
import { makeMicUnavailableBanner } from '@/voice/fallbacks/micUnavailable';
import { makeNoGetUserMediaBanner } from '@/voice/fallbacks/noGetUserMedia';
import type { VoiceStoreSlice } from '@/voice/types';

export type MicFailure = 'no-get-user-media' | 'mic-permission-denied' | 'mic-unavailable';

export type MicOutcome =
  | { readonly ok: true; readonly stream: MediaStream }
  | { readonly ok: false; readonly reason: MicFailure };

/**
 * Map a getUserMedia rejection to a failure kind.
 *
 * `NotAllowedError` covers both the visitor clicking Block and a managed
 * browser refusing on policy; the spec gives them the same name and the
 * remedy (change the permission) is the same either way. `SecurityError` is
 * the insecure-context refusal, which is also "you are not allowed", not
 * "your device is broken".
 */
export function classifyMicError(err: unknown): MicFailure {
  const name = typeof err === 'object' && err !== null && 'name' in err
    ? String((err as { name: unknown }).name)
    : '';
  return name === 'NotAllowedError' || name === 'SecurityError'
    ? 'mic-permission-denied'
    : 'mic-unavailable';
}

/** Announce that this browser cannot do voice at all, and stop offering it. */
export function applyUnsupported(store: VoiceStoreSlice): void {
  store.setVoiceMode('unsupported');
  showBanner(store, makeNoGetUserMediaBanner());
}

/**
 * Request the mic and move the store to match the result.
 *
 * `onRetry` is wired to the banner's retry button; callers pass their own
 * re-entry point so a refusal is recoverable without a page reload.
 */
export async function requestMic(
  store: VoiceStoreSlice,
  onRetry: () => void,
): Promise<MicOutcome> {
  if (detectVoiceCapability() === 'unsupported') {
    applyUnsupported(store);
    return { ok: false, reason: 'no-get-user-media' };
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // The mic works now, so any stale mic complaint is answered.
    clearBannerKinds(store, MIC_OWNED_KINDS);
    store.setVoiceMode('voice-active');
    return { ok: true, stream };
  } catch (err) {
    const reason = classifyMicError(err);
    showBanner(
      store,
      reason === 'mic-permission-denied'
        ? makeMicPermissionDeniedBanner(onRetry)
        : makeMicUnavailableBanner(onRetry),
    );
    store.setVoiceMode('fallen-back');
    return { ok: false, reason };
  }
}

/** Release every track on a stream. Safe to call twice. */
export function stopStream(stream: MediaStream | null): void {
  if (stream === null) return;
  for (const track of stream.getTracks()) track.stop();
}
